// @ts-nocheck
import { CloudUpload, X } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

// Custom styles for circular crop
const circularCropStyles = `
.profile-crop-circular .ReactCrop__crop-selection {
  border-radius: 50%;
  box-shadow: 0 0 0 9999em rgba(0, 0, 0, 0.5);
  border: 1px solid #fff;
}

/* Ensure the crop handles are visible outside the circular selection */
.profile-crop-circular .ReactCrop__drag-handle {
  width: 12px;
  height: 12px;
  background-color: #fff;
  border: 1px solid #333;
}

/* Improve visibility of the crop area */
.profile-crop-circular .ReactCrop__crop-selection {
  border: 2px solid #fff;
}

/* Add a subtle animation to highlight the circular nature */
.profile-crop-circular .ReactCrop__crop-selection {
  transition: all 0.3s ease;
}
`;

// Add styles to document
const styleElement = document.createElement('style');
styleElement.textContent = circularCropStyles;
if (typeof document !== 'undefined') {
  document.head.appendChild(styleElement);
}

type CropPreset = {
  name: string;
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  title: string;
  aspect: number | undefined;
  description: string;
};

const CROP_PRESETS: Record<string, CropPreset> = {
  custom: {
    name: 'custom',
    width: 800,
    height: 600,
    minWidth: 200,
    minHeight: 200,
    title: 'Imagem Personalizada',
    aspect: undefined,
    description: 'Recorte livre - Mínimo 200x200px'
  },
  logo: {
    name: 'logo',
    width: 400,
    height: 400,
    minWidth: 200,
    minHeight: 200,
    title: 'Logo',
    aspect: undefined,
    description: 'Recorte livre para logos - Mínimo 200x200px'
  },
  banner: {
    name: 'banner',
    width: 1200,
    height: 400,
    minWidth: 1200,
    minHeight: 400,
    title: 'Banner',
    aspect: 3,
    description: 'Proporção 3:1 - 1200x400px'
  },
  profile: {
    name: 'profile',
    width: 400,
    height: 400,
    minWidth: 400,
    minHeight: 400,
    title: 'Foto de Perfil',
    aspect: 1,
    description: 'Proporção 1:1 - 400x400px'
  }
};

interface ImageUploadProps {
  onImageUpload: (base64: string, preset: string) => void;
  preset: keyof typeof CROP_PRESETS;
  maxSizeMB?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUpload,
  preset,
  maxSizeMB = 5,
}) => {
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  // Initialize crop with appropriate values based on preset
  const [crop, setCrop] = useState<Crop>(() => {
    // For profile preset, ensure the crop is centered and square
    if (preset === 'profile') {
      return {
        unit: '%',
        width: 80,  // Slightly smaller to ensure visibility of the entire selection
        height: 80,
        x: 10,
        y: 10
      };
    }
    // Default crop for other presets
    return {
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5
    };
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validar tamanho
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      setError(`O arquivo deve ser menor que ${maxSizeMB}MB`);
      return;
    }

    // If already cropping or has a cropped image, confirm before replacing
    if (isCropping || croppedImage) {
      if (!window.confirm('Você já tem uma imagem selecionada. Deseja substituí-la?')) {
        return;
      }
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSourceImage(reader.result as string);
      setIsCropping(true);
      setError(null);

      // Initialize completedCrop based on preset
      if (preset === 'profile') {
        // For profile preset, ensure the initial completedCrop is centered and square
        setCompletedCrop({
          x: 10,
          y: 10,
          width: 80,
          height: 80,
          unit: '%'  // Use percentage for more responsive behavior
        });
      } else {
        // Default completedCrop for other presets
        setCompletedCrop({
          x: 5,
          y: 5,
          width: 90,
          height: 90,
          unit: '%'
        });
      }
    };
    reader.readAsDataURL(file);
  }, [maxSizeMB, isCropping, croppedImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const getCroppedImg = useCallback((
    image: HTMLImageElement,
    pixelCrop: PixelCrop,
    currentPreset: string = preset
  ): Promise<{ blob: Blob; url: string }> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // For profile preset, ensure the canvas is square for perfect circular crop
    if (currentPreset === 'profile') {
      const size = Math.max(pixelCrop.width, pixelCrop.height);
      canvas.width = size;
      canvas.height = size;
    } else {
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Clear the canvas before drawing
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // For profile preset (circular crop), create a circular clipping path
    if (currentPreset === 'profile') {
      // Create a circular clipping path
      ctx.save();
      ctx.beginPath();
      const radius = Math.min(canvas.width, canvas.height) / 2;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);
      ctx.clip();

      // Calculate offsets to center the image in the square canvas
      const offsetX = (canvas.width - pixelCrop.width) / 2;
      const offsetY = (canvas.height - pixelCrop.height) / 2;

      // Draw the image with the clipping path applied
      ctx.drawImage(
        image,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        offsetX,
        offsetY,
        pixelCrop.width,
        pixelCrop.height
      );
      ctx.restore();
    } else {
      // For non-profile presets, draw normally
      ctx.drawImage(
        image,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          const url = URL.createObjectURL(blob);
          resolve({ blob, url });
        },
        'image/jpeg',
        0.95
      );
    });
  }, [preset]);

  const handleCropComplete = useCallback(async () => {
    if (!imageRef.current || !completedCrop) return;

    try {
      const { blob, url } = await getCroppedImg(imageRef.current, completedCrop);

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setCroppedImage(url);
        setIsCropping(false);

        onImageUpload(base64String, preset);
      };
    } catch (error) {
      setError('Erro ao processar a imagem');
    }
  }, [completedCrop, getCroppedImg, onImageUpload, preset]);

  return (
    <div className="w-full  max-w-4xl ">
      {sourceImage && isCropping ? (
        <div className="space-y-4">
          <div className="flex justify-center overflow-hidden max-h-[600px] rounded-lg">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={CROP_PRESETS[preset].aspect}
              circularCrop={preset === 'profile'} /* Enable built-in circular crop for profile preset */
              className={`max-w-full ${preset === 'profile' ? 'profile-crop-circular' : ''}`}
            >
              <img
                ref={imageRef}
                src={sourceImage}
                alt="Upload"
                className="max-w-full max-h-[600px] object-contain"
              />
            </ReactCrop>
          </div>

          <div className="flex gap-4 justify-center">
            <button type='button'
              onClick={() => {
                setSourceImage(null);
                setCroppedImage(null);
                setIsCropping(false);
              }}
              className="px-6 py-2.5   text-gray-500 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Cancelar
            </button>
            <button type='button'
              onClick={handleCropComplete}
              className="px-6 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Confirmar
            </button>
          </div>
        </div>
      ) : croppedImage ? (
        <div className="space-y-4">
          <div className="relative flex justify-center">
            <img
              src={croppedImage}
              alt="Imagem cortada"
              className="max-w-full max-h-[600px] object-contain rounded-lg"
            />
            <button type='button'   
              onClick={() => {
                setSourceImage(null);
                setCroppedImage(null);
                setIsCropping(false);
              }}
              className="absolute -top-2 right-13 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors shadow-lg"
              title="Remover imagem"
            >
                <X size={20} className='text-white' strokeWidth={1} />
            </button>
          </div>
          <div className="flex justify-center">
            <button type='button'
              onClick={() => {
                setIsCropping(true);
                setCroppedImage(null);
              }}
              className="px-6 py-2.5 border-1 border-green-500 text-green-500 rounded-lg hover:bg-green-600/20 transition-colors"
            >
              Recortar Novamente
            </button>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg border-gray-700
            transition-all duration-200 ease-in-out
            ${isDragActive 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-green-400'}
          `}
        >
          <input {...getInputProps()} />
          <div className="p-5 text-center space-y-4">
            <div className="flex flex-col items-center">
                <CloudUpload size={50} className='text-green-500' strokeWidth={1} />
                <div className="flex justify-center">
                <p className=" text-sm text-lg font-medium text-gray-500">
                {isDragActive 
                  ? 'Solte a imagem ' 
                  : 'Arraste sua imagem '}
              </p>
              <p className=" mr-1 ml-1 text-sm text-gray-500"> ou </p>
              <button type='button' className=" text-sm font-bold   text-green-500 ">
                clique aqui 
              </button>
              </div>

              <p className="mt-4 text-[12px] text-gray-500">
                PNG, JPEG ou WebP (max. {maxSizeMB}MB)
              </p>
              <p className="text-[12px] text-gray-500">
                {CROP_PRESETS[preset].description}
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 rounded-lg">
          <p className="text-red-500 text-sm text-center">{error}</p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
